import { useEffect, useMemo, useState } from "react";
import usePersistentFilters from "../Common/usePersistentFilters";
import { useNavigate } from "react-router-dom";
import {
 Bar,
 BarChart,
 CartesianGrid,
 Cell,
 Line,
 LineChart,
 Pie,
 PieChart,
 ResponsiveContainer,
 Tooltip,
 XAxis,
 YAxis,
} from "recharts";
import api from "../../../api";
import cx360Logo from "../../../images/logo-full.png";
import "./TaskReportingSummary.css";

const defaultFilters = {
 search: "",
 employee: "",
 workOrder: "",
 project: "",
 status: "",
 priority: "",
 from: "",
 to: "",
};

const COLORS = ["#2f80ed", "#27ae60", "#f2994a", "#eb5757", "#9b51e0", "#6c757d"];

const toInputDate = (value) => {
 if (!value) return "";
 const date = new Date(value);
 if (Number.isNaN(date.getTime())) return "";
 return date.toISOString().split("T")[0];
};

const formatDate = (value) => {
 if (!value) return "-";
 const date = new Date(value);
 if (Number.isNaN(date.getTime())) return "-";
 return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

const formatPercent = (value) => `${Number(value || 0).toFixed(1)}%`;
const formatNumber = (value) => Number(value || 0).toLocaleString("en-IN");

const scoreLabel = (score) => {
 if (score >= 85) return "Excellent";
 if (score >= 70) return "Good";
 if (score >= 50) return "Average";
 return "Needs Attention";
};

const scoreClass = (score) => {
 if (score >= 85) return "score-excellent";
 if (score >= 70) return "score-good";
 if (score >= 50) return "score-average";
 return "score-low";
};

const TaskReportingSummary = () => {
 const navigate = useNavigate();
 const [filters, setFilters] = usePersistentFilters("task_reporting_summary_filters", defaultFilters);
 const [appliedFilters, setAppliedFilters] = useState(defaultFilters);
 const [summary, setSummary] = useState(null);
 const [employees, setEmployees] = useState([]);
 const [workOrders, setWorkOrders] = useState([]);
 const [loading, setLoading] = useState(true);
 const [exporting, setExporting] = useState(false);

 useEffect(() => {
 const loadEmployees = async () => {
 try {
 const response = await api.get("/employees");
 const data = response.data?.data || response.data?.employees || response.data || [];
 setEmployees(Array.isArray(data) ? data : []);
 } catch {
 setEmployees([]);
 }
 };

 const loadWorkOrders = async () => {
 try {
 const response = await api.get("/work-orders");
 const data = response.data?.data || response.data?.workOrders || response.data || [];
 setWorkOrders(Array.isArray(data) ? data : []);
 } catch {
 setWorkOrders([]);
 }
 };

 loadEmployees();
 loadWorkOrders();
 }, []);

 useEffect(() => {
 const loadSummary = async () => {
 setLoading(true);
 try {
 const params = new URLSearchParams();
 Object.entries(appliedFilters).forEach(([key, value]) => {
 if (value) params.set(key, value);
 });
 const response = await api.get(`/tasks/reporting-summary?${params.toString()}`);
 setSummary(response.data?.data || null);
 } catch (error) {
 setSummary(null);
 } finally {
 setLoading(false);
 }
 };

 loadSummary();
 }, [appliedFilters]);

 const projectOptions = useMemo(() => {
 const map = new Map();
 workOrders.forEach((workOrder) => {
 const project = workOrder.project;
 const projectId = project?._id || project;
 if (!projectId || map.has(String(projectId))) return;
 map.set(String(projectId), project?.projectName || project?.name || "Project");
 });
 return Array.from(map, ([value, label]) => ({ value, label }));
 }, [workOrders]);

 const workOrderOptions = useMemo(() => {
 return workOrders.map((workOrder) => ({
 value: workOrder._id,
 label: `${workOrder.woNo || workOrder.woNumber || workOrder._id}${workOrder.project?.projectName ? ` - ${workOrder.project.projectName}` : ""}`,
 }));
 }, [workOrders]);

 const employeeOptions = useMemo(() => {
 return employees.map((employee) => {
 const user = employee.userId || {};
 return {
 employeeId: employee._id,
 name: user.name || employee.name || "Employee",
 };
 });
 }, [employees]);

 const chartEmployees = useMemo(
 () => (summary?.topPerformers || []).slice(0, 8).map((employee) => ({
 name: String(employee.name || "").split(" ").slice(0, 1)[0] || "Employee",
 fullName: employee.name,
 score: employee.score || 0,
 completed: employee.completed || 0,
 delayed: (employee.delayedOpen || 0) + (employee.completedLate || 0),
 })),
 [summary]
 );

 const completionTrend = useMemo(
 () => summary?.completionTrend || [],
 [summary]
 );

 const statusBreakdown = useMemo(
 () => summary?.statusBreakdown || [],
 [summary]
 );

 const statusMix = useMemo(
 () => [
 { name: "Completed", value: summary?.overall?.completedTasks || 0, color: "#27ae60" },
 { name: "Delayed", value: summary?.overall?.delayedTasks || 0, color: "#eb5757" },
 { name: "In Progress", value: summary?.overall?.inProgressTasks || 0, color: "#2f80ed" },
 { name: "Assigned", value: summary?.overall?.assignedStatusTasks || 0, color: "#9b51e0" },
 { name: "Unassigned", value: summary?.overall?.unassignedTasks || 0, color: "#6c757d" },
 ].filter((item) => item.value > 0),
 [summary]
 );

 const updateFilter = (event) => {
 const { name, value } = event.target;
 setFilters((current) => ({ ...current, [name]: value }));
 };

 const applyFilters = () => {
 setAppliedFilters(filters);
 };

 const resetFilters = () => {
 setFilters(defaultFilters);
 setAppliedFilters(defaultFilters);
 };

 const setPreset = (days) => {
 const next = { ...filters };
 if (days === "all") {
 next.from = "";
 next.to = "";
 } else {
 const end = new Date();
 const start = new Date();
 start.setDate(start.getDate() - (Number(days) - 1));
 next.from = toInputDate(start);
 next.to = toInputDate(end);
 }
 setFilters(next);
 setAppliedFilters(next);
 };

 const handleExportPdf = async () => {
 if (!summary?.employees?.length) return;
 setExporting(true);
 try {
 const { PDFDocument, StandardFonts, rgb } = await import("pdf-lib");
 const pdfDoc = await PDFDocument.create();
 const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
 const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
 const logoBytes = await fetch(cx360Logo).then((response) => response.arrayBuffer());
 const logoImage = await pdfDoc.embedPng(logoBytes);
 const pageSize = [842, 595];
 const margin = 28;
 const contentWidth = pageSize[0] - margin * 2;
 const palette = {
 ink: rgb(0.11, 0.16, 0.25),
 muted: rgb(0.42, 0.48, 0.56),
 border: rgb(0.85, 0.88, 0.92),
 blue: rgb(0.18, 0.50, 0.93),
 green: rgb(0.16, 0.66, 0.39),
 amber: rgb(0.95, 0.60, 0.18),
 red: rgb(0.90, 0.30, 0.30),
 violet: rgb(0.45, 0.29, 0.78),
 slate: rgb(0.95, 0.97, 0.99),
 };
 const statusOrder = [
 ["Completed", summary.overall?.completedTasks || 0, palette.green],
 ["Delayed", summary.overall?.delayedTasks || 0, palette.amber],
 ["In Progress", summary.overall?.inProgressTasks || 0, palette.blue],
 ["Assigned", summary.overall?.assignedStatusTasks || 0, palette.violet],
 ["Unassigned", summary.overall?.unassignedTasks || 0, rgb(0.42, 0.48, 0.56)],
 ];

 const bytesToHuman = (value) => `${Number(value || 0).toLocaleString("en-IN")}`;
 const safeText = (value) => String(value ?? "").replace(/\s+/g, " ").trim();
 const truncateText = (text, max = 24) => {
 const value = safeText(text);
 return value.length > max ? `${value.slice(0, Math.max(0, max - 1))}…` : value;
 };
 const drawText = (page, text, x, y, options = {}) => {
 page.drawText(safeText(text), {
 x,
 y,
 size: options.size || 8,
 font: options.bold ? boldFont : font,
 color: options.color || palette.ink,
 maxWidth: options.maxWidth,
 lineHeight: options.lineHeight || 10,
 });
 };
 const drawCard = (page, x, y, width, height, title, value, accent) => {
 page.drawRectangle({
 x,
 y,
 width,
 height,
 color: rgb(1, 1, 1),
 borderColor: palette.border,
 borderWidth: 0.8,
 opacity: 1,
 });
 page.drawRectangle({ x, y: y + height - 8, width, height: 8, color: accent });
 drawText(page, title, x + 10, y + height - 24, { size: 8, color: palette.muted, bold: true });
 drawText(page, value, x + 10, y + 12, { size: 18, bold: true, color: palette.ink });
 };
 const addPage = (titleLine) => {
 const page = pdfDoc.addPage(pageSize);
 const yTop = pageSize[1] - margin;
 page.drawRectangle({ x: 0, y: pageSize[1] - 70, width: pageSize[0], height: 70, color: palette.ink });
 page.drawRectangle({ x: 0, y: pageSize[1] - 74, width: pageSize[0], height: 4, color: palette.blue });
 const logoWidth = 146;
 const logoHeight = (logoImage.height / logoImage.width) * logoWidth;
 const logoX = margin;
 const logoY = pageSize[1] - 60;
 page.drawImage(logoImage, {
 x: logoX,
 y: logoY - 1,
 width: logoWidth,
 height: logoHeight,
 });
 const titleSize = 18;
 const subtitleSize = 9;
 const titleWidth = boldFont.widthOfTextAtSize("Task Reporting Summary", titleSize);
 const subtitleWidth = font.widthOfTextAtSize(titleLine, subtitleSize);
 const centeredTitleX = (pageSize[0] - titleWidth) / 2;
 const centeredSubtitleX = (pageSize[0] - subtitleWidth) / 2;
 drawText(page, "Task Reporting Summary", centeredTitleX, pageSize[1] - 30, {
 size: titleSize,
 bold: true,
 color: rgb(1, 1, 1),
 });
 drawText(page, titleLine, centeredSubtitleX, pageSize[1] - 46, {
 size: subtitleSize,
 color: rgb(0.85, 0.90, 0.98),
 });
 return { page, yTop: pageSize[1] - 92 };
 };
 const drawTableHeader = (page, y) => {
 const columns = [
 { label: "Employee", width: 180 },
 { label: "Level", width: 56 },
 { label: "Assigned", width: 54 },
 { label: "Completed", width: 58 },
 { label: "Delayed", width: 52 },
 { label: "In Prog.", width: 54 },
 { label: "Comp. %", width: 54 },
 { label: "On Time %", width: 60 },
 { label: "Avg Delay", width: 58 },
 { label: "Score", width: 50 },
 ];
 let x = margin;
 page.drawRectangle({ x: margin, y: y - 2, width: contentWidth, height: 18, color: palette.slate });
 columns.forEach((column) => {
 drawText(page, column.label, x + 3, y + 2, { size: 7, bold: true, color: palette.muted });
 x += column.width;
 });
 return columns;
 };
 const drawStatusSection = (page, y) => {
 drawText(page, "Status Mix", margin, y, { size: 11, bold: true, color: palette.ink });
 let cursorY = y - 14;
 statusOrder.forEach(([label, value, color]) => {
 page.drawRectangle({ x: margin, y: cursorY - 2, width: contentWidth, height: 14, color: rgb(1, 1, 1), borderColor: palette.border, borderWidth: 0.4 });
 page.drawRectangle({ x: margin + 2, y: cursorY, width: 6, height: 6, color });
 drawText(page, label, margin + 14, cursorY - 1, { size: 7.5, color: palette.ink });
 drawText(page, bytesToHuman(value), margin + 250, cursorY - 1, { size: 7.5, bold: true, color: palette.ink });
 cursorY -= 14;
 });
 return cursorY;
 };

 const titleLine = [
 `Filters: ${summary.filters?.from ? `From ${formatDate(summary.filters.from)}` : "From All"}`,
 summary.filters?.to ? `To ${formatDate(summary.filters.to)}` : "To All",
 summary.filters?.workOrder ? `WO ${summary.filters.workOrder}` : "All Work Orders",
 summary.filters?.project ? `Project ${summary.filters.project}` : "All Projects",
 ].join(" | ");

 let { page, yTop } = addPage(titleLine);
 drawText(page, `Generated: ${new Date().toLocaleString("en-IN")}`, margin, yTop + 2, { size: 8, color: palette.muted });
 drawText(page, `Average Score: ${summary.averageScore || 0}`, margin + 260, yTop + 2, { size: 8, color: palette.muted });
 drawText(page, `Employees: ${summary.employees?.length || 0}`, margin + 390, yTop + 2, { size: 8, color: palette.muted });
 drawText(page, `Completion Rate: ${formatPercent(summary.overall?.completionRate || 0)}`, margin + 500, yTop + 2, { size: 8, color: palette.muted });

 const cardsY = yTop - 68;
 const cardW = (contentWidth - 20) / 3;
 const cardH = 56;
 drawCard(page, margin, cardsY, cardW, cardH, "Total Tasks", bytesToHuman(summary.overall?.totalTasks), palette.blue);
 drawCard(page, margin + cardW + 10, cardsY, cardW, cardH, "Completed", bytesToHuman(summary.overall?.completedTasks), palette.green);
 drawCard(page, margin + (cardW + 10) * 2, cardsY, cardW, cardH, "Delayed", bytesToHuman(summary.overall?.delayedTasks), palette.red);

 const secondCardY = cardsY - 66;
 drawCard(page, margin, secondCardY, cardW, cardH, "Completion Rate", formatPercent(summary.overall?.completionRate || 0), palette.violet);
 drawCard(page, margin + cardW + 10, secondCardY, cardW, cardH, "On Time Rate", formatPercent(summary.overall?.onTimeRate || 0), palette.green);
 drawCard(page, margin + (cardW + 10) * 2, secondCardY, cardW, cardH, "Avg Delay Days", bytesToHuman(summary.overall?.averageDelayDays || 0), palette.amber);

 const statusBottom = drawStatusSection(page, secondCardY - 20);
 drawText(page, "Employee Performance", margin, statusBottom - 12, { size: 11, bold: true, color: palette.ink });
 let tableY = statusBottom - 28;
 const columns = drawTableHeader(page, tableY);
 tableY -= 18;
 const rowHeight = 16;

 const drawRow = (row) => {
 let x = margin;
 const values = [
 truncateText(row.name, 28),
 row.level || "-",
 formatNumber(row.assignedTotal || 0),
 formatNumber(row.completed || 0),
 formatNumber((row.delayedOpen || 0) + (row.completedLate || 0)),
 formatNumber(row.inProgress || 0),
 formatPercent(row.completionRate || 0),
 formatPercent(row.onTimeRate || 0),
 formatNumber(row.avgDelayDays || 0),
 `${row.score || 0}`,
 ];
 values.forEach((value, index) => {
 drawText(page, value, x + 3, tableY + 3, {
 size: 7,
 bold: index === 0 || index === 9,
 color: index === 9 ? palette.blue : palette.ink,
 maxWidth: columns[index].width - 6,
 });
 x += columns[index].width;
 });
 page.drawLine({
 start: { x: margin, y: tableY - 1 },
 end: { x: pageSize[0] - margin, y: tableY - 1 },
 thickness: 0.3,
 color: palette.border,
 });
 tableY -= rowHeight;
 };

 (summary.employees || []).forEach((employee) => {
 if (tableY < margin + rowHeight) {
 ({ page, yTop } = addPage(titleLine));
 drawText(page, "Employee Performance (continued)", margin, yTop + 2, { size: 8, color: palette.muted });
 tableY = yTop - 10;
 drawTableHeader(page, tableY);
 tableY -= 18;
 }
 drawRow(employee);
 });

 const bytes = await pdfDoc.save();
 const blob = new Blob([bytes], { type: "application/pdf" });
 const url = URL.createObjectURL(blob);
 const link = document.createElement("a");
 link.href = url;
 link.download = "task-reporting-summary.pdf";
 link.click();
 URL.revokeObjectURL(url);
 } finally {
 setExporting(false);
 }
 };

 return (
 <div className="task-summary-page">
 <div className="task-summary-hero">
 <div>
 <p className="eyebrow">Task Reporting</p>
 <h3 className="mb-2">Task Reporting Summary</h3>
 <p className="text-muted mb-0">
 Employee workload, completion, delay, and performance overview with date, work order, and project filters.
 </p>
 </div>
 <div className="d-flex gap-2 flex-wrap">
 <button className="btn btn-outline-secondary" onClick={() => navigate(-1)}>
 Back
 </button>
 <button className="btn btn-outline-danger" onClick={handleExportPdf} disabled={exporting || !summary?.employees?.length}>
 {exporting ? "Exporting..." : "Export PDF"}
 </button>
 </div>
 </div>

 <div className="summary-preset-row">
 <button type="button" onClick={() => setPreset("7")} className="preset-btn">Last 7 Days</button>
 <button type="button" onClick={() => setPreset("30")} className="preset-btn">Last 30 Days</button>
 <button type="button" onClick={() => setPreset("90")} className="preset-btn">Last 90 Days</button>
 <button type="button" onClick={() => setPreset("all")} className="preset-btn">All Time</button>
 </div>

 <div className="summary-filters card shadow-sm mb-4">
 <div className="card-body">
 <div className="row g-3">
 {/* <div className="col-lg-3 col-md-6">
 <label className="form-label">Search</label>
 <input className="form-control" name="search" value={filters.search} onChange={updateFilter} placeholder="Task, employee, note" />
 </div> */}
 <div className="col-lg-2 col-md-6">
 <label className="form-label">Employee</label>
 <select className="form-select" name="employee" value={filters.employee} onChange={updateFilter}>
 <option value="">All Employees</option>
 {employeeOptions.map((employee) => (
 <option key={employee.employeeId} value={employee.employeeId}>{employee.name}</option>
 ))}
 </select>
 </div>
 <div className="col-lg-2 col-md-6">
 <label className="form-label">Work Order</label>
 <select className="form-select" name="workOrder" value={filters.workOrder} onChange={updateFilter}>
 <option value="">All Work Orders</option>
 {workOrderOptions.map((option) => (
 <option key={option.value} value={option.value}>{option.label}</option>
 ))}
 </select>
 </div>
 <div className="col-lg-2 col-md-6">
 <label className="form-label">Project</label>
 <select className="form-select" name="project" value={filters.project} onChange={updateFilter}>
 <option value="">All Projects</option>
 {projectOptions.map((option) => (
 <option key={option.value} value={option.value}>{option.label}</option>
 ))}
 </select>
 </div>
 <div className="col-lg-1 col-md-6">
 <label className="form-label">Status</label>
 <select className="form-select" name="status" value={filters.status} onChange={updateFilter}>
 <option value="">All</option>
 <option value="Assigned">Assigned</option>
 <option value="In Progress">In Progress</option>
 <option value="Delayed">Delayed</option>
 <option value="Completed On Time">Completed On Time</option>
 <option value="Completed Late">Completed Late</option>
 </select>
 </div>
 <div className="col-lg-1 col-md-6">
 <label className="form-label">Priority</label>
 <select className="form-select" name="priority" value={filters.priority} onChange={updateFilter}>
 <option value="">All</option>
 <option value="Critical">Critical</option>
 <option value="High">High</option>
 <option value="Medium">Medium</option>
 <option value="Low">Low</option>
 </select>
 </div>
 <div className="col-lg-1 col-md-6">
 <label className="form-label">From</label>
 <input type="date" className="form-control" name="from" value={filters.from} onChange={updateFilter} />
 </div>
 <div className="col-lg-1 col-md-6">
 <label className="form-label">To</label>
 <input type="date" className="form-control" name="to" value={filters.to} onChange={updateFilter} />
 </div>
 </div>
 <div className="d-flex gap-2 flex-wrap mt-3">
 <button className="btn btn-primary" onClick={applyFilters}>Apply Filters</button>
 <button className="btn btn-outline-secondary" onClick={resetFilters}>Reset</button>
 </div>
 </div>
 </div>

 {loading ? (
 <div className="text-center py-5">Loading reporting summary...</div>
 ) : !summary ? (
 <div className="alert alert-warning">No reporting data found.</div>
 ) : (
 <>
 <div className="row g-3 mb-4">
 <div className="col-md-4 col-lg-2">
 <div className="metric-card">
 <span>Total Tasks</span>
 <strong>{summary.overall?.totalTasks || 0}</strong>
 </div>
 </div>
 <div className="col-md-4 col-lg-2">
 <div className="metric-card">
 <span>Completed</span>
 <strong>{summary.overall?.completedTasks || 0}</strong>
 </div>
 </div>
 <div className="col-md-4 col-lg-2">
 <div className="metric-card">
 <span>Delayed</span>
 <strong>{summary.overall?.delayedTasks || 0}</strong>
 </div>
 </div>
 <div className="col-md-4 col-lg-2">
 <div className="metric-card">
 <span>Completion Rate</span>
 <strong>{formatPercent(summary.overall?.completionRate || 0)}</strong>
 </div>
 </div>
 <div className="col-md-4 col-lg-2">
 <div className="metric-card">
 <span>On Time Rate</span>
 <strong>{formatPercent(summary.overall?.onTimeRate || 0)}</strong>
 </div>
 </div>
 <div className="col-md-4 col-lg-2">
 <div className="metric-card">
 <span>Avg Delay Days</span>
 <strong>{summary.overall?.averageDelayDays || 0}</strong>
 </div>
 </div>
 </div>

 <div className="row g-4 mb-4">
 <div className="col-lg-4">
 <div className="summary-chart-card">
 <div className="d-flex justify-content-between align-items-center mb-3">
 <h5 className="mb-0">Status Mix</h5>
 <span className="text-muted small">All filtered tasks</span>
 </div>
 <div className="chart-box">
 <ResponsiveContainer width="100%" height={280}>
 <PieChart>
 <Pie data={statusMix} dataKey="value" nameKey="name" innerRadius={65} outerRadius={100} paddingAngle={4}>
 {statusMix.map((entry, index) => (
 <Cell key={`cell-${entry.name}`} fill={entry.color || COLORS[index % COLORS.length]} />
 ))}
 </Pie>
 <Tooltip />
 </PieChart>
 </ResponsiveContainer>
 </div>
 </div>
 </div>
 <div className="col-lg-4">
 <div className="summary-chart-card">
 <div className="d-flex justify-content-between align-items-center mb-3">
 <h5 className="mb-0">Top Performers</h5>
 <span className="text-muted small">Score ranking</span>
 </div>
 <div className="chart-box">
 <ResponsiveContainer width="100%" height={280}>
 <BarChart data={chartEmployees} layout="vertical" margin={{ left: 20, right: 20 }}>
 <CartesianGrid strokeDasharray="3 3" />
 <XAxis type="number" domain={[0, 100]} />
 <YAxis type="category" dataKey="name" width={90} />
 <Tooltip />
 <Bar dataKey="score" fill="#2f80ed" radius={[0, 8, 8, 0]} />
 </BarChart>
 </ResponsiveContainer>
 </div>
 </div>
 </div>
 <div className="col-lg-4">
 <div className="summary-chart-card">
 <div className="d-flex justify-content-between align-items-center mb-3">
 <h5 className="mb-0">Completion Trend</h5>
 <span className="text-muted small">Daily movement</span>
 </div>
 <div className="chart-box">
 <ResponsiveContainer width="100%" height={280}>
 <LineChart data={completionTrend}>
 <CartesianGrid strokeDasharray="3 3" />
 <XAxis dataKey="date" hide />
 <YAxis />
 <Tooltip />
 <Line type="monotone" dataKey="completed" stroke="#27ae60" strokeWidth={3} dot={false} />
 <Line type="monotone" dataKey="delayed" stroke="#eb5757" strokeWidth={3} dot={false} />
 </LineChart>
 </ResponsiveContainer>
 </div>
 </div>
 </div>
 </div>

 <div className="summary-table-card mb-4">
 <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
 <div>
 <h5 className="mb-1">Employee Performance</h5>
 <span className="text-muted small">
 Rank and performance for the current filter window
 </span>
 </div>
 <span className="text-muted small">
 {summary.employees?.length || 0} employees
 </span>
 </div>
 <div className="table-responsive">
 <table className="table align-middle summary-table">
 <thead>
 <tr>
 <th>Employee</th>
 <th>Level</th>
 <th>Assigned</th>
 <th>Completed</th>
 <th>Delayed</th>
 <th>In Progress</th>
 <th>Completion Rate</th>
 <th>On Time</th>
 <th>Avg Delay</th>
 <th>Score</th>
 <th>Last Task</th>
 </tr>
 </thead>
 <tbody>
 {summary.employees?.length ? (
 summary.employees.map((employee) => (
 <tr key={employee.employeeId}>
 <td>
 <div className="d-flex align-items-center gap-2">
 <div className="summary-avatar">
 {String(employee.name || "NA").split(" ").filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase()}
 </div>
 <div>
 <div className="fw-semibold">{employee.name}</div>
 <div className="text-muted small">{employee.role || "Employee"}</div>
 </div>
 </div>
 </td>
 <td>{employee.level || "-"}</td>
 <td>{employee.assignedTotal || 0}</td>
 <td>{employee.completed || 0}</td>
 <td>{(employee.delayedOpen || 0) + (employee.completedLate || 0)}</td>
 <td>{employee.inProgress || 0}</td>
 <td>{formatPercent(employee.completionRate || 0)}</td>
 <td>{formatPercent(employee.onTimeRate || 0)}</td>
 <td>{employee.avgDelayDays || 0}</td>
 <td>
 <span className={`score-pill ${scoreClass(employee.score)}`}>
 {employee.score || 0} / 100
 </span>
 <div className="text-muted small mt-1">{employee.performanceLabel || scoreLabel(employee.score)}</div>
 </td>
 <td>{formatDate(employee.lastTaskDate)}</td>
 </tr>
 ))
 ) : (
 <tr>
 <td colSpan="11" className="text-center text-muted py-4">
 No employee data for this filter.
 </td>
 </tr>
 )}
 </tbody>
 </table>
 </div>
 </div>
 </>
 )}
 </div>
 );
};

export default TaskReportingSummary;
